import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaAngleRight } from "react-icons/fa";
import { VscGraph } from "react-icons/vsc";
import { MdPlace } from "react-icons/md";
import { HiUsers } from "react-icons/hi";
import { BiMessageRoundedDetail } from "react-icons/bi";
import { BsFillFileRichtextFill } from "react-icons/bs";
import { IoMdNotifications } from "react-icons/io";
import logoSrc from "../../images/logo.png";

const Sidebar = () => {
  const location = useLocation();

  const menuItems = useMemo(
    () => [
      { title: "Dashboard", icon: <VscGraph size={18} />, path: "/dashboard" },
      {
        title: "Places",
        icon: <MdPlace size={18} />,
        children: [
          { title: "Actives", path: "/places/actives" },
          { title: "Drafts", path: "/places/drafts" },
          { title: "Collections", path: "/places/collections" },
          { title: "AI Discover", path: "/places/ai-discover" },
        ],
      },
      {
        title: "Notifications",
        icon: <IoMdNotifications size={18} />,
        children: [
          { title: "Promotions", path: "/notifications/promotions" },
          { title: "Announcements", path: "/notifications/announcements" },
          {
            title: "Push notifications",
            path: "/notifications/push-notifications",
          },
        ],
      },
      {
        title: "Users",
        icon: <HiUsers size={18} />,
        path: "/users",
      },
      {
        title: "Forum",
        icon: <BiMessageRoundedDetail size={18} />,
        path: "/forums",
      },

      {
        title: "Articles",
        icon: <BsFillFileRichtextFill size={18} />,
        path: "/articles",
      },
    ],
    [],
  );

  return (
    <aside className="flex flex-col bg-gradient-to-br from-gray-800 to-gray-700 text-gray-100 h-screen w-64">
      <div className="flex bg-gradient-to-r from-lime-500 to-sky-500 items-center justify-between p-4 h-16 ">
        <Link to="/dashboard" className="flex items-center space-x-2">
          <img src={logoSrc} alt="Logo" className="h-8 w-auto" />
          <div className="text-white font-bold text-lg drop-shadow-md">
            DOVKOLO ADMIN
          </div>
        </Link>
      </div>
      <nav className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide ">
        {menuItems.map((item, idx) => (
          <SidebarItem key={idx} item={item} activePath={location.pathname} />
        ))}
      </nav>
    </aside>
  );
};

const SidebarItem = ({ item, activePath }) => {
  const hasChildren = Array.isArray(item.children);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (
      hasChildren &&
      item.children.some((sub) => activePath.startsWith(sub.path))
    ) {
      setExpanded(true);
    }
  }, [activePath, hasChildren, item.children]);

  const linkPath = hasChildren ? item.children[0].path : item.path;

  const isActive = hasChildren
    ? item.children.some((sub) => activePath.startsWith(sub.path))
    : activePath === item.path;

  return (
    <div className="w-full">
      <div className="flex w-full">
        <Link
          to={linkPath}
          onClick={(e) => {
            if (hasChildren) {
              e.preventDefault();
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }
          }}
          className={`flex p-3 w-full relative font-light hover:opacity-100 hover:bg-gray-600 hover:text-white duration-200 ${isActive ? "text-white opacity-100" : "opacity-75"}`}
        >
          {hasChildren ? (
            <div className="flex items-center justify-between w-full">
              <div className="flex gap-2 items-center">
                <div
                  className={`absolute w-1 left-0 h-full ${isActive ? "bg-lime-400" : ""}`}
                />
                <div className="flex-shrink-0">{item.icon}</div>
                <div className="text-left">{item.title}</div>
              </div>
              <FaAngleRight
                className={`transform transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
                size={14}
              />
            </div>
          ) : (
            <div className="flex items-center w-full">
              <div
                className={`absolute w-1 left-0 h-full ${isActive ? "bg-lime-400" : ""}`}
              />
              <span className="flex-shrink-0">{item.icon}</span>
              <span className="ml-3 text-left">{item.title}</span>
            </div>
          )}
        </Link>
      </div>

      {hasChildren && expanded && (
        <div className="bg-gray-700">
          <div className=" flex flex-col">
            {item.children.map((sub, i) => {
              const subActive = activePath.startsWith(sub.path);
              return (
                <div key={i} className="flex">
                  <Link to={sub.path} className="w-full">
                    <div
                      className={`flex py-3 hover:text-white hover:opacity-100 hover:bg-gray-600 duration-200 ${subActive ? "bg-gray-600 opacity-100" : "opacity-75"}`}
                    >
                      <div
                        className={`flex ml-10 gap-2 items-center font-light text-sm`}
                      >
                        {sub.title}
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
